import { reviveNoteValue } from './noteTimestamp';
import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';

const publicCache = new Map();
let cacheOwner;

// Lecture directe Firestore des notes ordinaires, sans fonction cloud (≈ 5 s au
// premier appel d'une instance). Les règles n'autorisent une liste client que si
// elle filtre `akongoPrivate == false` et `confidential == false` : les notes
// antérieures au 08/09/2026, sans ces champs, seraient absentes du résultat.
// N'activer qu'APRÈS `functions/confidentialNotes/backfillPublicNoteMarkers.js`.
// Refus des règles ou index manquant : repli sur la fonction cloud.
export const publicNotesReadConfig = { direct: false };
const FALLBACK_CODES = new Set(['permission-denied', 'failed-precondition']);

async function readDirect(db, path, state) {
  let query = db.collection(path).where('akongoPrivate', '==', false).where('confidential', '==', false);
  for (const [field, op, value] of state.filters || []) query = query.where(field, op, value);
  for (const [field, direction] of state.orders || []) query = query.orderBy(field, direction);
  if (state.startAt != null) query = query.startAt(state.startAt);
  if (state.afterId) query = query.startAfter(await db.collection(path).doc(state.afterId).get());
  if (state.limit) query = query.limit(state.limit);
  const snapshot = await query.get();
  return snapshot.docs.map(doc => ({ id: doc.id, data: doc.data(), native: true }));
}

// A query-shaped adapter keeps each screen's ordinary scope/order/limit intact.
// No privacy equality constraints: legacy public notes need no backfill.
export function publicNotesQuery(db, path = 'notes', state = {}) {
  if (db.path) { path = db.path; db = db.firestore; }
  const next = patch => publicNotesQuery(db, path, { ...state, ...patch });
  return {
    where: (field, op, value) => next({ filters: [...(state.filters || []), [field, op, value]] }),
    orderBy: (field, direction = 'asc') => next({ orders: [...(state.orders || []), [field, direction]] }),
    limit: value => next({ limit: value }),
    startAfter: doc => next({ afterId: doc.id }),
    async get(options = {}) {
      const userId = () => db.app?.auth?.().currentUser?.uid;
      const uid = userId();
      if (cacheOwner !== uid) { publicCache.clear(); cacheOwner = uid; }
      const payload = { path, ...state };
      const key = JSON.stringify(payload);
      const makeSnapshot = rows => {
        const docs = rows.map(({ id, data, native }) => ({
          id, exists: true, ref: db.collection(path).doc(id), data: () => native ? data : reviveNoteValue(data),
        }));
        return { docs, size: docs.length, empty: docs.length === 0, forEach: callback => docs.forEach(callback) };
      };
      if (options.source === 'cache') return makeSnapshot(uid ? publicCache.get(key) || [] : []);
      let rows = null;
      if (publicNotesReadConfig.direct) {
        try { rows = await readDirect(db, path, state); }
        catch (error) {
          if (!FALLBACK_CODES.has(error?.code)) throw error;
          console.warn('[publicNotesQuery] direct read refused, callable fallback:', error.code, error.message);
        }
      }
      if (!rows) {
        const result = await firebase.app().functions('europe-west3').httpsCallable('queryPublicNotes')(payload);
        rows = result.data.docs;
      }
      if (userId() !== uid) throw new Error('Account changed while loading notes');
      rows = rows.filter(({ data }) => data.akongoPrivate !== true && data.confidential !== true);
      if (uid) publicCache.set(key, rows);
      return makeSnapshot(rows);
    },
  };
}
